import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import debounce from 'lodash/debounce';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import ReportFilters from 'components/ReportFilters/ReportFilters';
import DataTable from 'examples/Tables/DataTable';

import usePaginationHook from 'hooks/usePaginationHook';
import useReportsHook from 'hooks/useReportsHook';

import {
  appreciatedColumns,
  likedColumns,
  tradedColumns,
} from '../../../constants/columns.constants';
import { appreciatedRows, likedRows, tradedRows } from './Rows';

const PropertyReportsTab = ({ reportType }) => {
  const [selectedDuration, setSelectedDuration] = useState(null);

  const {
    currentPage,
    setCurrentPage,
    perPage,
    setPerPage,
    sortBy,
    setSortBy,
    searchTerm,
  } = usePaginationHook();

  const { fetchReportsData, reportsList, isLoading, getTabTitle } =
    useReportsHook({
      currentPage,
      perPage,
      sortBy,
      searchTerm,
      reportType,
      duration: selectedDuration,
    });

  const columns = useMemo(() => {
    switch (reportType) {
      case 'most-liked':
        return likedColumns;

      case 'most-appreciated':
        return appreciatedColumns;

      case 'most-traded':
        return tradedColumns;

      default:
        return [];
    }
  }, [reportType]);

  const rows = useMemo(() => {
    switch (reportType) {
      case 'most-liked':
        return reportsList?.reportsData?.length > 0
          ? reportsList?.reportsData?.map(likedRows)
          : [];

      case 'most-appreciated':
        return reportsList?.reportsData?.length > 0
          ? reportsList?.reportsData?.map(appreciatedRows)
          : [];

      case 'most-traded':
        return reportsList?.reportsData?.length > 0
          ? reportsList?.reportsData?.map(tradedRows)
          : [];

      default:
        return [];
    }
  }, [reportType, reportsList?.reportsData]);

  const onFetchDataDebounced = debounce(fetchReportsData, 0);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedDuration]);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, sortBy, perPage, searchTerm, selectedDuration]);

  const onChangeDuration = (value) => {
    setSelectedDuration(value);
  };

  return (
    <>
      <MDBox
        mx={2}
        mt={3}
        py={3}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          {getTabTitle()}
        </MDTypography>
      </MDBox>
      <ReportFilters
        reportType={reportType}
        onChangeDuration={onChangeDuration}
      />

      <MDBox
        pt={3}
        sx={{
          position: 'relative',
          paddingTop: '85px',
          paddingBottom: {
            xs: '150px',
            sm: '95px',
          },
        }}
      >
        <DataTable
          table={{ columns, rows }}
          isSorted={true}
          entriesPerPage={{ defaultValue: perPage }}
          showTotalEntries
          canSearch={false}
          // searchTerm={searchTerm}
          // setSearchTerm={setSearchTerm}
          perPage={perPage}
          setPerPage={setPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPage={reportsList?.totalPage}
          totalRecords={reportsList?.totalCount}
          // hiddenColumns={['id']}
          queryPageSortBy={[sortBy]}
          setSortBy={setSortBy}
          isLoading={isLoading}
        />
      </MDBox>
    </>
  );
};

PropertyReportsTab.propTypes = {
  reportType: PropTypes.string,
};

PropertyReportsTab.defaultValue = {
  reportType: '',
};

export default PropertyReportsTab;
